// Frontend error telemetry: report uncaught errors, unhandled promise
// rejections, and React error-boundary catches to the backend so they are
// visible centrally instead of only in the user's browser console.

const ENDPOINT = '/api/v1/client-errors'

// Avoid flooding the endpoint with repeats of the same error.
const seen = new Set<string>()

export function reportClientError(
  message: string,
  opts: { stack?: string; componentStack?: string; severity?: string } = {},
): void {
  try {
    const msg = String(message || '').slice(0, 2000)
    if (!msg) return
    const key = `${opts.severity || 'error'}:${msg}`
    if (seen.has(key)) return
    seen.add(key)
    if (seen.size > 100) seen.clear()

    const token = localStorage.getItem('token')
    const body = JSON.stringify({
      message: msg,
      stack: opts.stack ? String(opts.stack).slice(0, 8000) : undefined,
      component_stack: opts.componentStack ? String(opts.componentStack).slice(0, 8000) : undefined,
      url: window.location.href,
      severity: opts.severity || 'error',
    })

    // fetch with keepalive carries the auth header (so we capture who hit it)
    // and still completes if the page is navigating away.
    fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    }).catch(() => {
      // Last resort: sendBeacon (no auth header, but better than losing it).
      try { navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' })) } catch { /* noop */ }
    })
  } catch {
    // Telemetry must never throw.
  }
}

export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (e: ErrorEvent) => {
    reportClientError(e.message || 'window.onerror', { stack: e.error?.stack, severity: 'error' })
  })
  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason: any = e.reason
    reportClientError(
      String(reason?.message || reason || 'unhandledrejection'),
      { stack: reason?.stack, severity: 'unhandledrejection' },
    )
  })
}
